import { fitTitle, fitDescription } from "@/lib/seo-text";
import { createFileRoute, Link } from "@tanstack/react-router";

import { AgentUpdateSignup } from "@/components/agent-update-signup";
import { CtaBand, FaqList, PageHero } from "@/components/page-parts";
import { FIRM, faqSchema } from "@/lib/firm";

const TITLE = "Agent & Broker Update | QLD Property Law Briefing | Coastside Law";
const DESCRIPTION =
  "A short fortnightly email for Queensland real estate agents, brokers and advisers: Form 2 disclosure, AML, underwater sellers, bridging finance and settlement risk. Free to join.";

const TOPICS: ReadonlyArray<{ title: string; body: string }> = [
  {
    title: "Form 2 seller disclosure",
    body: "What has to be attached before signing, what a defective statement lets a buyer do, and where listings keep going wrong.",
  },
  {
    title: "AML Tranche 2",
    body: "What the reporting obligations actually mean for an individual agent, not just for the agency's compliance folder.",
  },
  {
    title: "Sellers who owe more than the house is worth",
    body: "How a shortfall payout is handled at settlement and how to keep a listing alive instead of losing it.",
  },
  {
    title: "Subject to sale and bridging finance",
    body: "Drafting that protects your seller when the buyer has to sell first, and what to check before recommending bridging.",
  },
  {
    title: "Mortgagee in possession sales",
    body: "What the contract strips out, what your buyer is really accepting, and the section 85 duty on the mortgagee.",
  },
  {
    title: "Deceased estates and capacity",
    body: "Whether a property can be listed before probate, and what to do when a seller's capacity is in question mid-campaign.",
  },
];

export const FAQS = [
  {
    q: "How often does the update come out?",
    a: "Every second Tuesday morning. One issue, one topic, around 300 words — you can read it before your sales meeting starts.",
  },
  {
    q: "Is it a sales email?",
    a: "No. Each issue is one practical Queensland property or estate issue explained plainly. There is no pitch and no offer, other than our standing free contract review.",
  },
  {
    q: "Who is it for?",
    a: "Real estate agents, mortgage brokers, accountants and financial planners working on the Sunshine Coast, in Moreton Bay and across the Wide Bay. Anyone whose clients sign property contracts.",
  },
  {
    q: "Can you present this at our sales meeting?",
    a: `Yes. Michael and Erin regularly run free 20-minute sessions at agency sales meetings on disclosure, AML and settlement risk. Call ${FIRM.phone} to book one.`,
  },
  {
    q: "How do I unsubscribe?",
    a: "Every email carries a one-click unsubscribe link. We do not sell, rent or share the list with anyone.",
  },
];

export const Route = createFileRoute("/agent-updates")({
  head: () => ({
    meta: [
      { title: fitTitle(TITLE) },
      { name: "description", content: fitDescription(DESCRIPTION) },
      { property: "og:title", content: fitTitle(TITLE) },
      { property: "og:description", content: fitDescription(DESCRIPTION) },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://coastsidelaw.com.au/agent-updates" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: "https://coastsidelaw.com.au/agent-updates" }],
    scripts: [{ type: "application/ld+json", children: JSON.stringify(faqSchema(FAQS)) }],
  }),
  component: AgentUpdatesPage,
});

function AgentUpdatesPage() {
  return (
    <>
      <PageHero
        eyebrow="For agents, brokers and advisers"
        title="The fortnightly agent & broker update"
        lead="One short email every second Tuesday on a Queensland property or estate issue that is affecting deals right now. Written by solicitors, not marketers."
      />

      <section className="mx-auto max-w-3xl px-5 py-16">
        <AgentUpdateSignup />

        <p className="mt-6 text-sm leading-relaxed text-muted-foreground">
          Working with property buyers? <Link to="/mortgage-broker-conveyancing-qld" className="font-semibold text-primary underline">Talk to Coastside about mortgage broker referrals</Link>.
        </p>

        <div className="mt-14">
          <h2 className="font-serif text-3xl text-foreground">What you&apos;ll get</h2>
          <div className="mt-6 grid gap-4 sm:grid-cols-2">
            {TOPICS.map((topic) => (
              <div key={topic.title} className="rounded-sm border border-border bg-card p-5">
                <p className="font-serif text-xl text-foreground">{topic.title}</p>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{topic.body}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-14 rounded-sm border-l-2 border-accent bg-secondary/50 p-7">
          <h2 className="font-serif text-2xl text-foreground">Bring us to your sales meeting</h2>
          <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
            We run a free 20-minute session for agency sales meetings across Caloundra, the
            Sunshine Coast and the Redcliffe Peninsula — seller disclosure, AML obligations and the
            contract terms that cost agencies settlements. Call {FIRM.phone} or{" "}
            <Link to="/contact" className="font-semibold text-primary hover:underline">
              send us a message
            </Link>
            .
          </p>
        </div>

        <div className="mt-14">
          <h2 className="font-serif text-3xl text-foreground">Questions people ask</h2>
          <div className="mt-6">
            <FaqList items={FAQS} />
          </div>
          <p className="mt-8 text-sm text-muted-foreground">
            Prefer to read at your own pace? Every issue draws on our{" "}
            <Link to="/guides" className="font-semibold text-primary hover:underline">
              library of Queensland legal guides
            </Link>
            .
          </p>
        </div>
      </section>

      <CtaBand title="Need a contract looked at today?" />
      <div className="h-20" />
    </>
  );
}
